import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect } from 'react';
import Select from 'react-select';

import { CalendarDays } from 'lucide-react';
import Moment from 'moment-timezone';
import { Button, Col, Row, FormGroup, Input, Checkbox, InputGroupText, InputGroup, Popover, PopoverTrigger, PopoverContent, DatePickerMoment } from 'twyne-new.ui'

import appStore from 'stores/app';
import { defaultDateTimeFormat } from 'utils';

const propTypes = {
  disabled: PropTypes.bool,
  meridiemSystem: PropTypes.bool,
  checkbox: PropTypes.bool,
  time: PropTypes.bool,
  value: PropTypes.any,
  mask: PropTypes.string,
  onChange: PropTypes.func,
};

const meridiemOpts = ['am', 'pm'].map(value => ({ value, label: value.toUpperCase() }))

export default class DateTimePicker extends React.Component {
  get calendarId() {
    const { name } = this.props
    return `btnCalendar-${name}`
  }

  get disabled() {
    const { checkbox, value } = this.props
    return checkbox && value == null
  }

  Time = () => {
    const { value: date, meridiemSystem, onChange } = this.props
    const hour = +date.format('H')
    const hourm = +date.format('h')
    const minute = +date.format('m')
    const meridiem = date.format('a')

    const handleChangeField = (field, value) => {
      date.set(field, value)
      this.setState({})
      onChange(date)
    }

    const handleChange = field => evt => {
      const value = +evt.target.value
      handleChangeField(field, value)
    }

    return <Row className='px-3 m-0'>
      <Col className='px-1'>
        <FormGroup >
          <Input value={meridiemSystem ? hourm : hour} onChange={handleChange('hour')} type='number' max={meridiemSystem ? '12' : '23'} min={meridiemSystem ? '1' : '0'} />
        </FormGroup>
      </Col>
      <Col className='px-1'>
        <FormGroup>
          <Input value={minute} onChange={handleChange('minute')} type='number' max='59' min='0' />
        </FormGroup>
      </Col>
      {meridiemSystem && <Col className='px-1'>
        <FormGroup>
          <Select value={meridiem} options={meridiemOpts} clearable={false}
            onChange={({ value }) => {
              if (value === meridiem) return;
              handleChangeField('hour', hour + (value === 'pm' ? 12 : -12))
            }} />
        </FormGroup>
      </Col>}
    </Row>
  }

  Checker = () => {
    const { onChange, value, checkbox } = this.props

    if (!checkbox) {
      return null
    }

    const [checked, setChecked] = useState(!!value)

    const handleChange = checked => {
      setChecked(checked)
      onChange(checked ? Moment.tz(appStore.timezone) : null)
    }

    return <InputGroupText addonType='prepend'>
      <Checkbox
        disabled={this.props.disabled}
        onChange={handleChange}
        value={checked}
      />
    </InputGroupText>
  }

  Calendar = ({ onChangeFocus }) => <>
    <InputGroupText addonType='append'>
      <Button variant='ghost' className='p-0' disabled={this.props.disabled || this.disabled} id={this.calendarId} onClick={() => onChangeFocus(true)}>
        <CalendarDays size={18} />
      </Button>
    </InputGroupText>
  </>

  InputGroupWrapper = ({ children, className, onChangeFocus }) => <>
    <InputGroup className={className}>
      <this.Checker />
      {children}
      <this.Calendar onChangeFocus={onChangeFocus} />
    </InputGroup>
  </>

  DateInput = ({ date, onChange, toggleFocus, mask }) => {
    const [value, setValue] = useState(null)

    const formatDate = date => {
      setValue(date && date.format(mask) || null)
    }

    const handleChange = evt => {
      const { value } = evt.target
      setValue(value)
    }

    const handleParseDate = useCallback(evt => {
      toggleFocus(false)
      const { value } = evt.target
      const newdate = Moment.tz(value, [mask, Moment.ISO_8601], appStore.timezone)
      if (value && newdate.isValid()) {
        onChange(newdate)
      } else {
        formatDate(date)
      }
    }, [date])

    useEffect(() => {
      formatDate(date)
    }, [date])

    return <Input
      value={!value ? "Date" : value}
      onChange={handleChange}
      onBlur={handleParseDate}
      onFocus={() => toggleFocus(true)}
      disabled={this.props.disabled || !value}
      className='h-full'
    />
  }

  Picker = ({ onChange, value, mask, open, onChangeFocus, onChangeInputFocus, ...props }) => {
    const handleChangeFocus = useCallback(() => {
      !this.props.disabled && !this.disabled && onChangeFocus(focused => !focused)
    }, [value])

    const handleChange = date => {
      onChange(date && Moment(date).tz(appStore.timezone))
    }

    return <>
      <Popover open={open} onOpenChange={handleChangeFocus}>
        <PopoverTrigger>
          <div className='h-full'>
            <this.DateInput date={value} onChange={onChange} toggleFocus={onChangeInputFocus} mask={mask} />
          </div>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-0' align='start'>
          <DatePickerMoment
            mode='single'
            value={value && value.tz(appStore.timezone)}
            onChange={handleChange}
            numberOfMonths={1}
            {...props}
          />
        </PopoverContent>
      </Popover>
    </>
  }

  Wrapper = ({ checkbox, className, props }) => {
    const [focused, setFocused] = useState(false)
    const [inputFocused, setInputFocused] = useState(false)

    return <this.InputGroupWrapper {...{ checkbox, className }} onChangeFocus={setFocused} >
      <this.Picker {...props} open={focused || inputFocused} onChangeFocus={setFocused} onChangeInputFocus={setInputFocused} />
    </this.InputGroupWrapper>
  }

  render() {
    const { checkbox, className, ...props } = this.props

    return <this.Wrapper {...{ checkbox, className, props }} />
  }
}

DateTimePicker.propTypes = { ...DatePickerMoment.propTypes, ...propTypes }
DateTimePicker.defaultProps = {
  checkbox: false,
  time: false,
  mask: defaultDateTimeFormat,
  meridiemSystem: false,
}
